<template>
  <navigation-layout
    :class="{ '!translate-x-0': isSidebarOpened }"
    :level="2"
    class="translate-x-full duration-200"
    is-sub-layout
    title="Новое тестирование"
  >
    <template #header>
      <app-button size="small" @click="handleBackClick" type="texted" variant="tertiary" icon class="absolute left-2.5 top-1/2 -translate-y-1/2 w-10 !p-0">
        <img
          alt=""
          class="img--hoar !w-5 !h-5"
          src="@/assets/icons/direction/arrow-right-long.svg"
        />
      </app-button>
    </template>

    <template #default>
      <div class="m-1.5">
        <settings-questions-tabs />
      </div>
      <div class="grid grid-cols-3 gap-1.5 border-y border-y-lunar p-1.5">
        <action-button
          v-for="action in CREATE_ACTIONS"
          :key="action.value"
          :action="action"
          :active="action.value === CreateAction.CREATE"
          :disabled="isPreviewLoading || isDraftLoading || isCreateLoading"
          :loading="resolveCurrentActionLoading(action.value as CreateAction)"
          @click="resolveCurrentAction(action.value as CreateAction)"
        />
      </div>
    </template>
  </navigation-layout>
</template>
<script lang="ts" setup>
import { computed, ref } from "vue";
import NavigationLayout from "@/layouts/navigation/navigation-layout.vue";
import { useSidebarStore } from "@/stores/sidebar/sidebar";
import { storeToRefs } from "pinia";
import { SidebarType } from "@/layouts/sidebars/sidebars.types";
import SettingsQuestionsTabs from "@/layouts/sidebars/knowledge-base-create-sidebar/settings-questions-tabs.vue";
import {
  CREATE_ACTIONS,
  CreateAction,
} from "@/layouts/sidebars/knowledge-base-create-sidebar/knowledge-testing-survey-sidebar.data";
import ActionButton from "@/modules/knowledge-base/views/testing-survey/components/action-button.vue";
import { useKnowledgeBaseStore } from "@/stores/knowledge-base/knowledge-base";
import { testingApi } from "@/api";
import { useSnackbarStore } from "@/stores/snackbar/snackbar";
import { useRouter } from "vue-router";
import { Route } from "@/router/router.types";
import type { QuestionFormDTO } from "@boostbiz/question_form_microservice/client/schemas";
import AppButton from "@/shared/UIKit/app-button/app-button.vue";
import { TestingTab } from "@/modules/knowledge-base/views/testing-survey/testing/tabs/testing-tabs.data";
import { SnackbarStatus } from "@/stores/snackbar/snackbar.types";

const router = useRouter();

const { showSnackbar } = useSnackbarStore();

const sidebarStore = useSidebarStore();
const knowledgeBaseStore = useKnowledgeBaseStore();

const { currentSidebars } = storeToRefs(sidebarStore);

const isSidebarOpened = computed(
  () =>
    currentSidebars.value[currentSidebars.value.length - 1] ===
    SidebarType.KNOWLEDGE_BASE_TESTING_CREATE,
);


const handleBackClick = () => {
  const confirmed = window.confirm("Вы уверены, что хотите завершить создание?\nВсе изменения будут утеряны.");

  if (confirmed) {
    sidebarStore.goBack();
    router.push({
      path: `/${Route.KNOWLEDGE_BASE_TESTING}`,
      query: {
        tab: TestingTab.TESTS_LIST,
        page: 1,
        count: 10,
        search: "",
      },
    });
  }

};

const isCreateLoading = ref(false);
const isPreviewLoading = ref(false);
const isDraftLoading = ref(false);

const resolveCurrentActionLoading = (action: CreateAction) => {
  switch (action) {
    case CreateAction.CREATE:
      return isCreateLoading.value;
    case CreateAction.DRAFT:
      return isDraftLoading.value;
    case CreateAction.PREVIEW:
      return isPreviewLoading.value;
  }
};

const createTest = async () => {
  if (
    knowledgeBaseStore.testFormRef!.validate() &&
    knowledgeBaseStore.testQuestionsRef!.validate()
  ) {
    isCreateLoading.value = true;

    try {
      const testId = await testingApi.actions.createTestingForm(
        knowledgeBaseStore.test,
      );

      await testingApi.actions.startByOwner(testId);

      showSnackbar("Тестирование успешно создано и запущено");

      await router.push({
        path: `/${Route.KNOWLEDGE_BASE_TESTING}`,
        query: {
          tab: TestingTab.TESTS_LIST,
          page: 1,
          count: 10,
          search: "",
        },
      });
    } catch (e) {
      showSnackbar("Не удалось создать тестирование", SnackbarStatus.ERROR);
      console.error(e);
    } finally {
      isCreateLoading.value = false;
    }
  }
};

const sendToDraftTest = async () => {
  isDraftLoading.value = true;
  try {
    await testingApi.actions.createTestingForm(knowledgeBaseStore.test);
    sidebarStore.goBack();
    await router.push({
      path: `/${Route.KNOWLEDGE_BASE_TESTING}`,
      query: {
        tab: TestingTab.TESTS_LIST,
        page: 1,
        count: 10,
        search: "",
      },
    });

    showSnackbar("Тестирование успешно сохранено в черновик");
  } catch (e) {
    showSnackbar("Не удалось сохранить тестирование в черновик", SnackbarStatus.ERROR);
    console.error(e);
  } finally {
    isDraftLoading.value = false;
  }
};

const previewTest = async () => {
  isPreviewLoading.value = true;
  knowledgeBaseStore.testPreview =
    knowledgeBaseStore.test as unknown as QuestionFormDTO;
  await router.push(`/${Route.KNOWLEDGE_BASE_TESTING_PREVIEW}`);
};

const resolveCurrentAction = (action: CreateAction) => {
  switch (action) {
    case CreateAction.CREATE:
      return createTest();
    case CreateAction.DRAFT:
      return sendToDraftTest();
    case CreateAction.PREVIEW:
      return previewTest();
  }
};
</script>

<style lang="scss" scoped></style>
